import { DateInput } from '@/components/ui/DateInput'
import { IconCalendar } from '@tabler/icons-react'
import { useEffect, useRef } from 'react'
import { dueSeverity, MONO, QUEUE_COLOR } from '@/pages/CareCoordination/queueTheme'
import { useUpdateItemValue, type CellProps } from '../hooks'

// DateInput re-parses the field on every keystroke, so a date being TYPED arrives as a
// run of complete-looking dates ("Sep 18, 2" parses, and so does "Sep 18, 20"). Each one
// used to be written immediately: measured at 7 PATCHes for one typed date, two of which
// came back 500 "could not serialize access due to concurrent update" and surfaced as
// "Failed to update task". Waiting out the typing collapses that to a single write of the
// value the user actually finished on. A calendar pick is one change, so it just waits
// this long before saving.
const COMMIT_DELAY_MS = 400

export const DateCell = ({ item, column }: CellProps) => {
  const { update } = useUpdateItemValue(item)
  const raw = item.values[column.id]
  const stored = typeof raw === 'string' ? raw : null

  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Read inside the timeout, which outlives the render that scheduled it.
  const latestStored = useRef(stored)
  latestStored.current = stored
  const pending = useRef<{ value: string | null } | null>(null)

  const commitNow = () => {
    clearTimeout(timer.current)
    const edit = pending.current
    pending.current = null
    if (edit && edit.value !== latestStored.current) void update(column.id, edit.value)
  }
  // The effect below runs its cleanup on unmount only, so it would otherwise capture the
  // first render's closure; the ref keeps it pointed at the current one.
  const commitNowRef = useRef(commitNow)
  commitNowRef.current = commitNow
  // Flush rather than discard: marking the task Done or changing a filter unmounts the row,
  // and a cancelled timer would drop an edit the user had already finished typing.
  useEffect(() => () => commitNowRef.current(), [])

  const commitLater = (next: string | null) => {
    pending.current = { value: next }
    clearTimeout(timer.current)
    timer.current = setTimeout(commitNow, COMMIT_DELAY_MS)
  }
  // Built-in columns are keyed by their type on every board the app creates.
  const severity = dueSeverity(stored, item.values.status === 'Done')

  return (
    <div className="flex w-full flex-col items-start justify-center gap-1 pr-3">
      <div className="flex w-full items-center gap-2">
        {/* Fixed slot, not a flex-sized one (PD-1497). The clear (X) is the input's
            own right section, so it sits at the input's right edge — and letting
            flex size the input moved that edge by ~66px depending on whether an
            aging badge sat beside it. A fixed width pins the X to the same offset
            on every row, badge or no badge. */}
        {/* Wide enough for "MMM D, YYYY" so the year never truncates (PD-1684). */}
        <div className="w-[140px] shrink-0">
          <DateInput
            value={stored}
            // Mantine v8 DateInput passes a 'YYYY-MM-DD' string (or null on clear).
            onChange={(value) => commitLater(value || null)}
            // PD-1684 — tasks are scheduled a year or more ahead, so the year is what
            // tells two otherwise identical due dates apart. It also makes the TYPED
            // entry path unambiguous: DateInput parses typed text against this format.
            valueFormat="MMM D, YYYY"
            size="xs"
            variant="unstyled"
            clearable
            // Leading calendar icon signals the empty cell is clickable (matches the
            // Assignee/Status affordances); the date text sits just after it.
            placeholder="Set date"
            leftSection={<IconCalendar size={14} style={{ color: QUEUE_COLOR.subtle }} />}
            leftSectionWidth={20}
            rightSectionWidth={18}
            classNames={{ input: 'h-5 min-h-0 pl-0 text-[12.5px]' }}
            styles={{ input: { fontFamily: MONO, color: severity.dateColor } }}
          />
        </div>
        {/* Aging chip: how late, not merely that it is late — the number is what
            decides whether a task gets escalated or worked next. */}
        {severity.chip && (
          <span
            // The exact lateness drives what gets worked next, so it must stay
            // recoverable even in the pathological case where the badge has to
            // truncate (a five-digit day count) rather than overlap.
            title={severity.chip.label}
            className="min-w-0 truncate rounded px-[7px] py-[3px] text-[11px] tracking-[0.03em]"
            style={{
              fontFamily: MONO,
              background: severity.chip.bg,
              color: severity.chip.color,
            }}
          >
            {severity.chip.label}
          </span>
        )}
      </div>
      {severity.hint && (
        <span className="ml-5 text-[11px] leading-none" style={{ color: QUEUE_COLOR.subtle }}>
          {severity.hint}
        </span>
      )}
    </div>
  )
}
